import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { UNIT_OF_MEASURE_LABELS, UnitOfMeasureType } from '@app/models/item/enum/UnitOfMeasureType';
import { Batch, BatchSearchCommand, BatchStatus } from '@app/models/production/batch/batch';
import { BatchProxyService } from '@app/proxies/production/batch.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

@Component({
  selector: 'app-batches',
  templateUrl: './batches.component.html',
  styleUrl: './batches.component.scss',
})
export class BatchesComponent {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly proxyService = inject(BatchProxyService);
  private readonly enumService = inject(EnumService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  batches = signal<Batch[]>([]);
  readonly filterState = createDynamicFilterState({ storageKey: 'batches-filters', config: this.filterConfig });
  showFilters = signal(false);

  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  statusOptions: EnumValues = this.enumService.getEnum('BatchStatus', 'productionEnums')?.values ?? {};
  private filtersPanelInitialized = false;
  private currentSearchCriteria: BatchSearchCommand = {};

  readonly BatchStatus = BatchStatus;

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });
  }

  // Rebuilds the filter labels whenever the language changes; the config holds resolved strings, not keys.
  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.initializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  redirectToDetails(id?: string): void {
    this.router.navigate(['/batch-details'], { queryParams: { id: id ?? 'new' } });
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private initializeFilterConfig(): void {
    if (Object.keys(this.statusOptions).length === 0) {
      this.statusOptions = this.enumService.getEnum('BatchStatus', 'productionEnums')?.values ?? {};
    }

    this.filterConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.localeHelperService.translateWithFallback('Batch_Number', 'Number'),
          placeholder: this.localeHelperService.translateWithFallback('Batch_Enter_Number', 'Enter batch number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: this.localeHelperService.translateWithFallback('Status', 'Status'),
          placeholder: this.localeHelperService.translateWithFallback('Select_Status', 'Select Status'),
          options: this.statusOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'productDevelopmentNumber',
          label: this.localeHelperService.translateWithFallback('Batch_Recipe', 'Product Recipe'),
          placeholder: this.localeHelperService.translateWithFallback('Batch_Enter_Recipe', 'Enter recipe number'),
          defaultVisible: false,
        },
      ],
    });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.currentSearchCriteria = {
      number: (filters['number'] as string) || undefined,
      status: this.readStatuses(filters['statuses']),
      productDevelopmentNumber: (filters['productDevelopmentNumber'] as string) || undefined,
    };
    this.currentPage.set(1);
    this.searchBatches(this.currentSearchCriteria);
  }

  // The multiselect yields either the raw values or the option objects, depending on how it was restored.
  private readStatuses(value: unknown): number[] | undefined {
    if (!Array.isArray(value) || value.length === 0) return undefined;
    const first = value[0];
    if (typeof first === 'object' && first !== null && 'value' in first) {
      return (value as { value: number }[]).map((item) => item.value);
    }
    return value as number[];
  }

  private searchBatches(criteria: BatchSearchCommand): void {
    this.incrementLoading();

    this.proxyService
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          this.batches.set(response.success && response.data ? response.data.items : []);
          this.totalRecords.set(response.success && response.data ? response.data.totalCount : 0);
        },
        error: () => {
          this.batches.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.searchBatches(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) return;

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.searchBatches(this.currentSearchCriteria);
  }

  getStatusSeverity(status: BatchStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case BatchStatus.Approved:
      case BatchStatus.Completed:
      case BatchStatus.Closed:
        return 'success';
      case BatchStatus.ReadyForProduction:
      case BatchStatus.InProduction:
        return 'info';
      case BatchStatus.Draft:
        return 'warn';
      case BatchStatus.Cancelled:
      case BatchStatus.Deleted:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getStatusLabel(status: BatchStatus): string {
    return this.statusOptions[status] ?? `Status ${status}`;
  }

  getUomLabel(uom: number): string {
    return UNIT_OF_MEASURE_LABELS[uom as UnitOfMeasureType] ?? `uom:${uom}`;
  }
}
